import { Box, Typography } from "@mui/material";
import { doc, onSnapshot } from "firebase/firestore";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../context/auth";
import AuthLoadingState from "./auth/AuthLoadingState";
import { I18nContext } from "../context/i18n";
import { firestore } from "../shared/firebase";
import { langToLocale } from "../lib/i18n-registry";

type MaintenanceBlockerSettings = {
  enabled?: boolean;
  returnAt?: string | number | null | { toDate?: () => Date };
};

const normalizeReturnAt = (
  value: MaintenanceBlockerSettings["returnAt"],
) => {
  if (!value) return null;
  if (typeof value === "object" && typeof value.toDate === "function") {
    return value.toDate();
  }
  if (typeof value === "object") return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const BLOCKER_BYPASS_PATHS = new Set([
  "/login",
  "/subscribe",
  "/verify-account",
]);

const isBlockableHubPath = (pathname: string) => {
  if (BLOCKER_BYPASS_PATHS.has(pathname)) return false;
  if (pathname.startsWith("/admin")) return false;
  if (pathname.startsWith("/org")) return false;
  return true;
};

const isAnonymousBlockableHubPath = (pathname: string) =>
  pathname === "/community" || pathname.startsWith("/community-");

const HubMaintenanceBlocker = ({ children }: { children: ReactNode }) => {
  const { user, loadingAuth, isImpersonatingFromAdmin } =
    useContext(AuthContext);
  const { loadTranslations, getI18nValue, lang } = useContext(I18nContext);
  const { pathname } = useLocation();
  const [i18n, setI18n] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [settingsApply, setSettingsApply] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [returnAt, setReturnAt] = useState<Date | null>(null);

  const isAdminUser = user?.userType === "admin";
  // Listening and blocking are separate on purpose. The flag is watched for
  // anyone who could ever be blocked -- including while they sit on a bypass
  // page like /verify-account -- so it is already known when they move on.
  // Tying the subscription to the current page used to tear it down on every
  // bypass page and flash the dark loading screen on the next navigation.
  const shouldSubscribe =
    !isAdminUser &&
    !isImpersonatingFromAdmin &&
    (!!user || isAnonymousBlockableHubPath(pathname));
  const shouldApplyBlocker = shouldSubscribe && isBlockableHubPath(pathname);

  useEffect(() => {
    let active = true;
    loadTranslations("components", "components").then((values) => {
      if (active) setI18n(values?.hubMaintenanceBlocker ?? {});
    });

    return () => {
      active = false;
    };
  }, [lang, loadTranslations]);

  useEffect(() => {
    if (!shouldSubscribe) {
      setLoading(false);
      setSettingsApply(false);
      setEnabled(false);
      setReturnAt(null);
      return;
    }

    setLoading(true);
    setSettingsApply(true);
    const unsubscribe = onSnapshot(
      doc(firestore, "Settings", "mobileMaintenanceBlocker"),
      (snapshot) => {
        const data = snapshot.data() as MaintenanceBlockerSettings | undefined;
        setEnabled(snapshot.exists() && data?.enabled === true);
        setReturnAt(normalizeReturnAt(data?.returnAt));
        setLoading(false);
      },
      (error) => {
        console.error("Failed to subscribe to hub maintenance blocker:", error);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [shouldSubscribe]);

  const returnAtText = useMemo(() => {
    if (!returnAt) return null;
    const template = getI18nValue(
      i18n,
      "returnAt",
      "We'll be back on {date}",
    );
    return template.replace(
      "{date}",
      returnAt.toLocaleString(langToLocale(lang), {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    );
  }, [getI18nValue, i18n, lang, returnAt]);

  if (!shouldApplyBlocker) return <>{children}</>;

  // Still waiting on the flag: almost always "off", so this is the same
  // quiet splash as the session restore (nothing for 300ms, then the logo),
  // not the dark maintenance takeover below.
  if (!settingsApply || loading || (enabled && loadingAuth)) {
    return <AuthLoadingState />;
  }

  if (!enabled) return <>{children}</>;

  // This screen is a fixed full-bleed takeover (maintenance lockout),
  // deliberately dark regardless of the app's light/dark scheme — like
  // VerifyView. None of its colours have an exact scheme-token match
  // (background.default is #ffffff in light, not #050505) and mapping them
  // would invert this intentionally-dark screen in light mode. Left as
  // literals; see phase2-report.md.
  return (
    <Box
      sx={{
        alignItems: "center",
        backgroundImage:
          "linear-gradient(rgba(68, 68, 68, 0.82), rgba(68, 68, 68, 0.82)), url('/images/background.jpg')",
        backgroundPosition: "center",
        backgroundSize: "cover",
        bgcolor: "#050505",
        color: "#fff",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        px: 3,
        textAlign: "center",
      }}
    >
      <Box sx={{ maxWidth: 420, width: "100%" }}>
        <Box
          component="img"
          src="/images/logo-white.svg"
          alt="Hooper"
          sx={{ height: 74, mb: 3, maxWidth: "100%", width: 210 }}
        />
        <Typography
          sx={{
            fontSize: { xs: 22, sm: 24 },
            fontWeight: 800,
            lineHeight: 1.35,
          }}
        >
          {getI18nValue(i18n, "message", "We are in maintenance")}
        </Typography>
        {returnAtText ? (
          <Typography
            sx={{
              fontSize: { xs: 16, sm: 17 },
              fontWeight: 700,
              lineHeight: 1.45,
              mt: 1.5,
              opacity: 0.9,
            }}
          >
            {returnAtText}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export default HubMaintenanceBlocker;
